/**
 * Lightweight client-side auth.
 *
 * Holds the currently signed-in user (name + role) and the JWT token.
 * Both are persisted to localStorage so a hard page refresh (Ctrl+Shift+R)
 * or opening a new tab keeps the user logged in until the JWT expires or
 * they explicitly sign out.
 */
export type Role = 'admin' | 'employee' | string

export type SoftwareCode = 'SHAKTHI_POWER' | 'GRAND_SHAKTHI_POWER'

export interface AuthUser {
  name: string
  email: string
  role: Role
  /** Which applications this user is allowed into — set by Employee Master's
   *  Software Access tab, embedded in the JWT at login. */
  softwareAccess?: SoftwareCode[]
}

const TOKEN_KEY = 'sp_auth_token'
const USER_KEY  = 'sp_auth_user'

// ── Restore from localStorage on module load ─────────────────────────────
let currentUser: AuthUser | null = (() => {
  try {
    const raw = localStorage.getItem(USER_KEY)
    return raw ? (JSON.parse(raw) as AuthUser) : null
  } catch {
    return null
  }
})()

let currentToken: string | null = localStorage.getItem(TOKEN_KEY)

/**
 * Is the persisted token still usable?
 *  • offline demo tokens ("sp-local-…") are always treated as valid
 *  • real JWTs are decoded and checked against their `exp` claim
 *  • anything missing or unparseable counts as invalid (logged out)
 */
export function isTokenValid(): boolean {
  const token = currentToken
  if (!token) return false
  if (token.startsWith('sp-local-')) return true
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    if (payload.exp && Date.now() / 1000 >= payload.exp) {
      // Token expired — clean up so the user is redirected to login
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(USER_KEY)
      currentToken = null
      currentUser  = null
      return false
    }
    return true
  } catch {
    return false
  }
}

export const authStore = {
  setUser(u: AuthUser): void {
    currentUser = u
    localStorage.setItem(USER_KEY, JSON.stringify(u))
  },

  setToken(token: string): void {
    currentToken = token
    localStorage.setItem(TOKEN_KEY, token)
  },

  getToken(): string | null {
    return currentToken
  },

  clear(): void {
    currentUser  = null
    currentToken = null
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
  },

  /** Current signed-in user; returns null if nobody is logged in. */
  getUser(): AuthUser | null {
    return currentUser
  },

  /**
   * Logged in only if BOTH a user record AND a still-valid token are present.
   */
  isLoggedIn(): boolean {
    return currentUser !== null && isTokenValid()
  },

  isAdmin(): boolean {
    return (currentUser?.role || '').toLowerCase() === 'admin'
  },

  hasSoftwareAccess(code: SoftwareCode): boolean {
    return !!currentUser?.softwareAccess?.includes(code)
  },
}

